import { BudgetItemComputed } from '../types/apiTypes';

/** The two fields the visibility rule reads. Keeps the helpers usable from any bucket shape. */
type BucketTargetAndBalance = Pick<
  BudgetItemComputed,
  'budget_bucket_limit' | 'amount'
>;

/**
 * A bucket with no target and no money in it.
 *
 * The case this exists for is a bucket an admin turned off. Disabling resolves the bucket's
 * target to $0 and the disable sweep moves its balance out to the others, so it reaches the
 * frontend as 0/0 — nothing to draw, and nothing the customer could act on.
 *
 * Deliberately tested on the numbers rather than on the override flag: `admin_bucket_enabled` is
 * scoped out of the customer payload on purpose and should stay that way, because a customer is
 * not told an admin adjusted their budget. 0/0 is the observable shadow of that state. It also
 * catches a customer's own zero-target bucket, which is equally empty of information.
 *
 * Note the `&&`. A bucket with no target but a non-zero balance is NOT hidden — that is money the
 * customer still has, and hiding it would be hiding their own funds. Callers that divide by the
 * target must still guard, because that case reaches them.
 *
 * Known and accepted: this reads balance and target only, not `spent`/`saved`. Those are
 * month-scoped allocation sums that survive a disable — and the sweep writes a negative allocation
 * on the bucket it empties — so a bucket disabled part-way through a month arrives here as 0/0
 * while still carrying that month's activity, and gets hidden along with it. The row reappears
 * correctly absent from the next month onward, once those allocations fall outside the window.
 * Deliberate call: a disabled bucket is not worth a row on either surface, and widening the rule
 * would mean rendering a targetless bucket for which none of the bar states apply. Do not "fix"
 * this by adding spent/saved without revisiting that.
 */
export const isEmptyBucket = (item: BucketTargetAndBalance): boolean => {
  const goal = item?.budget_bucket_limit ?? 0;
  const net = item?.amount ?? 0;
  return goal === 0 && net === 0;
};

/**
 * The buckets worth rendering, in order.
 *
 * Shared so the two surfaces that draw buckets cannot drift apart. They read different endpoints
 * — home's "Budgeted Savings" (SavingsWidget) and the Finances tab's "Monthly Budgeted Savings"
 * (BudgetCategories) — and before this only home filtered, so a disabled bucket disappeared from
 * one view while the other drew it as a completed savings goal.
 *
 * Generic so it preserves the caller's element type: Finances passes `MonthlyBudgetMetrics`, home
 * passes `BudgetItemComputed`, and neither should be widened by going through here.
 */
export const visibleBudgetBuckets = <T extends BucketTargetAndBalance>(
  items: T[] | undefined | null
): T[] => (items ?? []).filter((item) => !isEmptyBucket(item));
